import React, { useMemo } from "react";
import "../Terms/Terms.css";
import { useLocalContext } from "../../context/LocalContext";

const RefundPolicy = () => {
  const { webinfo = {} } = useLocalContext();

  const brand = useMemo(
    () => ({
      name: webinfo.name || "Your Brand",
      email: webinfo.email || "hello@example.com",
      phone: webinfo.phone || "+1 (000) 000-0000",
      phonecall: webinfo.phonecall || "+10000000000",
      address: webinfo.address || "United States",
    }),
    [webinfo]
  );

  const effectiveDate = useMemo(
    () =>
      new Date().toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      }),
    []
  );

  return (
    <section className="terms">
      <div className="terms-wrap">
        <header className="terms-head">
          <span className="terms-kicker">Legal</span>
          <h1>Refund Policy</h1>
          <p className="terms-meta">
            <span>Provider: {brand.name}</span>
            <span>Last updated: {effectiveDate}</span>
            <span>Applicable to: United States</span>
          </p>
        </header>

        <div className="terms-content">
          <TermsBlock title="1. Overview">
            <p>
              This Refund Policy describes when and how {brand.name} issues refunds for services and any associated payments. We aim to be fair and consistent and to comply with applicable U.S. consumer protection standards where they apply.
            </p>
          </TermsBlock>

          <TermsBlock title="2. Eligibility for refunds">
            <p>
              Refund eligibility depends on the type of engagement and the stage of work:
            </p>
            <ul>
              <li><strong>Deposits or upfront payments:</strong> May be partially or fully refundable if we have not yet begun substantive work, as set out in your agreement or proposal.</li>
              <li><strong>Completed work:</strong> Once deliverables have been accepted or the scope has been completed as agreed, fees for that work are generally non-refundable.</li>
              <li><strong>Cancellation by you:</strong> If you cancel after work has started, we may refund any portion of fees that corresponds to work not yet performed, minus any non-recoverable costs we have already incurred.</li>
              <li><strong>Cancellation by us:</strong> If we are unable to complete the engagement for reasons within our control, we will refund any fees paid for work not delivered.</li>
            </ul>
          </TermsBlock>

          <TermsBlock title="3. Timeframe for refund requests">
            <p>
              Refund requests must be submitted in writing (e.g., by email to {brand.email}) within <strong>30 days</strong> of the date of the relevant payment or completion of the relevant phase of work, unless your contract specifies a different period. We will respond to your request within a reasonable time, typically within 10 business days.
            </p>
          </TermsBlock>

          <TermsBlock title="4. How refunds are processed">
            <p>
              Approved refunds will be issued using the same method of payment used for the original transaction, where possible. Processing may take 5–10 business days (or longer for some payment providers). We do not charge a fee for processing eligible refunds.
            </p>
          </TermsBlock>

          <TermsBlock title="5. Non-refundable items">
            <p>
              The following are typically non-refundable: third-party costs we have already paid on your behalf (e.g., licenses, subscriptions, or stock assets), administrative or setup fees if stated in your agreement, and any work that has been delivered and accepted.
            </p>
          </TermsBlock>

          <TermsBlock title="6. Disputes and chargebacks">
            <p>
              If you are not satisfied, we encourage you to contact us first so we can try to resolve the issue. Unauthorized chargebacks or payment disputes may result in suspension of services and recovery actions in accordance with applicable law.
            </p>
          </TermsBlock>

          <TermsBlock title="7. Changes to this policy">
            <p>
              We may update this Refund Policy from time to time. The “Last updated” date at the top reflects the most recent version. Continued use of our services after changes constitutes acceptance of the updated policy.
            </p>
          </TermsBlock>

          <TermsBlock title="8. Contact">
            <ul className="terms-contact">
              <li>
                <strong>Email:</strong>{" "}
                <a href={`mailto:${brand.email}`}>{brand.email}</a>
              </li>
              <li>
                <strong>Phone:</strong>{" "}
                <a href={`tel:${brand.phonecall}`}>{brand.phone}</a>
              </li>
              <li>
                <strong>Address:</strong> {brand.address}
              </li>
            </ul>
          </TermsBlock>
        </div>
      </div>
    </section>
  );
};

const TermsBlock = ({ title, children }) => (
  <section className="terms-card">
    <h2>{title}</h2>
    {children}
  </section>
);

export default RefundPolicy;
